'use client';

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import useAuthStore from '@/store/useAuthStore';
import { FEATURE_LABELS, TIER_LABELS, getBillingIntervalLabel, type BillingInterval, type GatedFeature, type SubscriptionTier } from '@/constant/subscription';

interface AskParentCheckoutModalProps {
  visible: boolean;
  onClose: () => void;
  suggestedTier?: SubscriptionTier | null;
  suggestedBillingInterval?: BillingInterval | null;
  sourceFeature?: GatedFeature | null;
  connectedParentEmail?: string | null;
  lockParentEmail?: boolean;
}

export default function AskParentCheckoutModal({
  visible,
  onClose,
  suggestedTier,
  suggestedBillingInterval,
  sourceFeature,
  connectedParentEmail,
  lockParentEmail = false,
}: AskParentCheckoutModalProps) {
  const profile = useAuthStore((s) => s.profile);
  const [mounted, setMounted] = useState(false);
  const [parentEmail, setParentEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const studentName = useMemo(() => (
    String(profile?.nickname || profile?.onboard_data?.profile?.firstName || profile?.email || 'your student')
      .trim()
      .split('@')[0]
  ), [profile]);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (lockParentEmail) {
      setParentEmail(String(connectedParentEmail || '').trim());
      setError(null);
    }
  }, [connectedParentEmail, lockParentEmail, visible]);

  if (!visible || !mounted) return null;

  const planCopy = suggestedTier && suggestedTier !== 'free'
    ? `${TIER_LABELS[suggestedTier]}${suggestedBillingInterval ? ` · ${getBillingIntervalLabel(suggestedBillingInterval)}` : ''}`
    : null;
  const featureLabel = sourceFeature ? FEATURE_LABELS[sourceFeature] : null;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/stripe/parent-checkout-intents', {
        parentEmail,
        suggestedTier,
        suggestedBillingInterval,
        sourceFeature,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send parent checkout link.');
      }
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send parent checkout link.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setParentEmail('');
    setError(null);
    setSent(false);
    setSending(false);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-[520px] rounded-[28px] bg-white px-6 pb-6 pt-5 shadow-[0_24px_60px_rgba(17,24,39,0.2)]">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#667085] hover:text-[#344054]"
          aria-label="Close parent checkout modal"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF2D6] text-[#D38D09]">
          <Mail className="h-6 w-6" />
        </div>

        {sent ? (
          <div className="mt-5 text-center">
            <h2 className="display-text-bold text-[2rem] leading-tight text-[#1E1E20]">Link sent</h2>
            <p className="display-text mt-3 text-[16px] leading-7 text-[#6B7280]">
              {lockParentEmail
                ? <>We emailed your connected parent at {parentEmail} so they can continue this purchase for {studentName}.</>
                : <>We emailed {parentEmail} a secure link so they can continue this purchase for {studentName}.</>}
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="display-text-bold mt-6 inline-flex min-w-[220px] items-center justify-center rounded-full bg-[#43CF2C] px-6 py-3.5 text-white"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="mt-5 text-center">
              <h2 className="display-text-bold text-[2rem] leading-tight text-[#1E1E20]">
                {lockParentEmail ? 'Continue with Parent' : 'Ask Parent to Continue'}
              </h2>
              <p className="display-text mt-3 text-[16px] leading-7 text-[#6B7280]">
                {lockParentEmail
                  ? <>Your parent account is already connected. We&apos;ll send them a secure link to continue this purchase for {studentName}.</>
                  : <>Enter your parent&apos;s email and we&apos;ll send them a secure link to continue this purchase for {studentName}.</>}
              </p>
              {planCopy || featureLabel ? (
                <p className="display-text mt-2 text-[14px] leading-6 text-[#A16E00]">
                  {planCopy ? `Suggested plan: ${planCopy}` : null}
                  {planCopy && featureLabel ? ' · ' : null}
                  {featureLabel ? `From: ${featureLabel}` : null}
                </p>
              ) : null}
            </div>

            <div className="mt-6">
              <label className="display-text-semi mb-2 block text-[15px] text-[#344054]">Parent email</label>
              <input
                type="email"
                value={parentEmail}
                onChange={(event) => setParentEmail(event.target.value)}
                placeholder="parent@example.com"
                disabled={lockParentEmail}
                className="display-text w-full rounded-[18px] border border-[#D0D5DD] px-4 py-4 text-[18px] text-[#1E1E20] outline-none transition-colors focus:border-[#FBAF2C] disabled:bg-[#F8FAFC] disabled:text-[#667085]"
              />
              {error ? <p className="display-text mt-2 text-[14px] text-[#DC2626]">{error}</p> : null}
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleClose}
                className="display-text-bold inline-flex items-center justify-center rounded-full bg-[#ECEFF1] px-6 py-3.5 text-[#344054]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={sending}
                className="display-text-bold inline-flex items-center justify-center gap-2 rounded-full bg-[#43CF2C] px-6 py-3.5 text-white disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {sending ? 'Sending...' : lockParentEmail ? 'Send to connected parent' : 'Send link'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
